import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { useAuth } from "@/contexts/AuthContext";
import { supabase } from "@/integrations/supabase/client";
import { Loader2, Eye, EyeOff, Building2 } from "lucide-react";
import { Checkbox } from "@/components/ui/checkbox";
import { clearLocalAuthSession, getFriendlyAuthErrorMessage, getSessionWithTimeout } from "@/lib/auth-errors";
interface SubdomainLoginProps {
  organization: {
    id: string;
    name: string;
    slug: string;
    logo_url: string | null;
    primary_color: string | null;
    secondary_color: string | null;
  };
}

export default function SubdomainLogin({ organization }: SubdomainLoginProps) {
  const { signIn } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  // Load remembered email on mount
  useEffect(() => {
    const savedEmail = localStorage.getItem(`trainly_remember_${organization.slug}`);
    if (savedEmail) {
      setEmail(savedEmail);
      setRememberMe(true);
    }
  }, [organization.slug]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!email.trim() || !password.trim()) {
      setError("Vennligst fyll inn e-post og passord");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const { error: signInError } = await signIn(email, password);
      if (signInError) throw signInError;

      // Verify the user is a member of this organization
      const { data: { session } } = await getSessionWithTimeout(4000);
      const user = session?.user;
      if (!user) throw new Error("Kunne ikke hente brukerdata");

      // Check super admin + org membership in parallel
      const [{ data: superAdmin }, { data: membership }] = await Promise.all([
        supabase
          .from("super_admins")
          .select("id")
          .eq("user_id", user.id)
          .maybeSingle(),
        supabase
          .from("organization_members")
          .select("id")
          .eq("user_id", user.id)
          .eq("organization_id", organization.id)
          .maybeSingle(),
      ]);

      if (!superAdmin) {
        if (!membership) {
          await clearLocalAuthSession();
          setError("Du har ikke tilgang til denne organisasjonen. Kontakt din administrator.");
          return;
        }
      }

      // Save or clear remembered email
      if (rememberMe) {
        localStorage.setItem(`trainly_remember_${organization.slug}`, email);
      } else {
        localStorage.removeItem(`trainly_remember_${organization.slug}`);
      }

      navigate(`/${organization.slug}`);
    } catch (err: any) {
      setError(getFriendlyAuthErrorMessage(err, "Feil e-post eller passord"));
    } finally {
      setLoading(false);
    }
  };

  const primaryColor = organization.primary_color || "hsl(var(--primary))";

  return (
    <div className="min-h-screen flex items-center justify-center bg-background p-4">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="w-full max-w-md"
      >
        {/* Organization Branding */}
        <div className="text-center mb-8">
          {organization.logo_url ? (
            <img
              src={organization.logo_url}
              alt={organization.name}
              className="h-16 w-auto mx-auto mb-4"
            />
          ) : (
            <div
              className="h-16 w-16 rounded-xl flex items-center justify-center mx-auto mb-4"
              style={{ backgroundColor: `${primaryColor}20` }}
            >
              <Building2 className="h-8 w-8" style={{ color: primaryColor }} />
            </div>
          )}
          <h1 className="text-2xl font-bold text-foreground">{organization.name}</h1>
          <p className="text-muted-foreground mt-1">Logg inn på din konto</p>
        </div>

        {/* Login Form */}
        <div className="bg-card rounded-xl shadow-card p-6 border">
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="text-sm font-medium text-foreground">E-post</label>
              <Input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="mt-1"
                autoComplete="email"
              />
            </div>

            <div>
              <label className="text-sm font-medium text-foreground">Passord</label>
              <div className="relative mt-1">
                <Input
                  type={showPassword ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  className="pr-10"
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Checkbox
                id="remember-me"
                checked={rememberMe}
                onCheckedChange={(checked) => setRememberMe(checked === true)}
              />
              <label htmlFor="remember-me" className="text-sm text-muted-foreground cursor-pointer select-none">
                Husk meg
              </label>
            </div>

            {error && (
              <p className="text-sm text-destructive bg-destructive/10 p-3 rounded-lg">
                {error}
              </p>
            )}

            <Button
              type="submit"
              className="w-full"
              disabled={loading}
              style={{ backgroundColor: primaryColor }}
            >
              {loading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  Logger inn...
                </>
              ) : (
                "Logg inn"
              )}
            </Button>
          </form>
        </div>

        <p className="text-center text-sm text-muted-foreground mt-6">
          Trenger du hjelp? Kontakt din administrator
        </p>
      </motion.div>
    </div>
  );
}
